import { useMemo } from 'react';
import { Col, Row, Dropdown } from 'react-bootstrap';
import { MEDIAURL } from '../../utilities/constants';
import { virtiusV } from '../helpers/logos';
import { useSelector } from 'react-redux';
import {
  convertMillipointsToDisplay,
  useTeamsByApparatusData,
} from '../../utilities/scoring';
import {
  numToApparatus,
  abbvToApparatusName,
  teamBrand,
} from '../../utilities/conversions';
import { producerActions } from '../../redux/_actions';
import { useDispatch } from 'react-redux';
import { SessionTeamScoreType } from '../../models';
import { useDisplayConfig, useLineups, useSession } from './hooks';
import { isIntrasquad } from '../../utilities/session';
import * as Icons from '../helpers/icons';
import TeamScoreCard from './teamscorecard';
import useTeamLayoutMeasure from './hooks/useTeamLayoutMeasure';
import './teambanner.css';
import producerStyles from './producer.module.css';

export function TeamBannerSelector({
  teams,
  selected,
  side,
  name,
  logo,
  lineupId,
  scoresheet = false,
}) {
  const dispatch = useDispatch();

  const handleChange = (teamId, side) => {
    dispatch(producerActions.teamBannerChange(teamId, side));
  };

  return (
    <Dropdown
      className={['teamBannerSelector', scoresheet ? 'scoresheet' : null].join(
        ' '
      )}
    >
      <Dropdown.Toggle variant="outline">
        <Row>
          {scoresheet ? <Col xs={2} className="logo-spacer" /> : null}
          <Col className="vCenter logo" xs={scoresheet ? 2 : 3}>
            {logo ? (
              <img
                src={`${MEDIAURL}${logo}`}
                alt={name ? `${name} logo` : ''}
              />
            ) : (
              virtiusV
            )}
          </Col>
          <Col xs={scoresheet ? 8 : 9} className="vCenter team">
            {name ? name : 'Team ?'}
          </Col>
        </Row>
      </Dropdown.Toggle>
      <Dropdown.Menu
        className="teamBannerSelector-menu"
        data-display="static"
        align={side === 'teamB' ? 'right' : 'left'}
      >
        <ul>
          {teams.map((team, index) => {
            const { displayName } = team || {};
            const { logo } = teamBrand(team);
            const isSelected = selected === index && lineupId === team.lineupId;

            return (
              <li key={`${side}_${index}_${name}`}>
                <Dropdown.Item
                  as="button"
                  variant="outline"
                  disabled={isSelected}
                  onSelect={() => handleChange(index, side)}
                >
                  <Col xs={3}>
                    {logo ? (
                      <img
                        src={`${MEDIAURL}${logo}`}
                        alt={displayName ? `${displayName} logo` : ''}
                      />
                    ) : (
                      virtiusV
                    )}
                  </Col>
                  <Col xs={9}>
                    <div className={[isSelected ? 'selected' : null].join(' ')}>
                      {displayName}
                    </div>
                  </Col>
                </Dropdown.Item>
              </li>
            );
          })}
        </ul>
      </Dropdown.Menu>
    </Dropdown>
  );
}

export function TeamBanner({ teamSide }) {
  const teamId = useSelector((state) => state.producer[teamSide].teamId);
  const lineupId = useSelector((state) => state.producer[teamSide].lineupId);
  const teamScoringType = useSelector((state) => state.session.teamScoring);
  const producerScores = useSelector((state) => state.producer.scores);
  const shouldShowScores =
    producerScores && teamScoringType !== SessionTeamScoreType.NONE;
  const round = useSelector((state) => state.producer.round);
  const gender = useSelector((state) => state.session.gender);
  const apparatus = numToApparatus(round, true, gender);
  const apparatusIcon = Icons?.[`${apparatus}noText`];
  const displayConfig = useDisplayConfig();

  const sessionId = useSelector((state) => state.session.id);
  const teamsData = useTeamsByApparatusData();

  // Use role-based session query to handle authorization properly
  const session = useSession(sessionId);

  // Skip rendering if session data is not available yet
  if (!session || !session.id) {
    return null;
  }

  const scoreData = teamsData(session);
  const teams = scoreData.teams.sort((a, b) => a.order - b.order);
  const team = teams.find((t) => t.lineupId === lineupId);
  const { displayName, finalScore } = team || {};
  const { logo } = teamBrand(team);

  return (
    <div className="teamBanner">
      <Row style={{ height: '100%' }}>
        <Col xs={8} className="vCenter">
          <TeamBannerSelector
            name={`${displayName}${
              shouldShowScores
                ? ''
                : ` » ${abbvToApparatusName[apparatus].toUpperCase()}`
            }`}
            teams={teams}
            selected={teamId}
            side={teamSide}
            lineupId={lineupId}
            logo={logo}
          ></TeamBannerSelector>
        </Col>
        <Col className="vCenter score" xs={4}>
          {shouldShowScores
            ? finalScore ||
              convertMillipointsToDisplay(0, displayConfig.scorePrecision)
            : apparatusIcon}
        </Col>
      </Row>
    </div>
  );
}

export function TeamBannerCompact() {
  const sessionId = useSelector((state) => state.session.id);
  const teamsData = useTeamsByApparatusData();

  // Use role-based session query to handle authorization properly
  const session = useSession(sessionId);

  // Memoize teams data with pre-parsed logos to avoid re-parsing on every render
  const teams = useMemo(() => {
    if (!session?.id) return [];
    const scoreData = teamsData(session);
    return scoreData.teams
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((team) => ({
        ...team,
        parsedLogo:
          (team.logos && JSON.parse(team.logos)?.metaData?.filename) || null,
      }));
  }, [session, teamsData]);

  // Use space-aware layout measurement (compact teams need ~120px each)
  const { useTwoRows, containerRef } = useTeamLayoutMeasure({
    teamCount: teams.length,
    minTeamWidth: 120,
    breakToTwoRowsAtTeams: 6, // Only force two rows at 6+ teams
  });

  // Skip rendering if session data is not available yet
  if (!session || !session.id) {
    return null;
  }

  const midpoint = Math.ceil(teams.length / 2);
  const topRowTeams = useTwoRows ? teams.slice(0, midpoint) : teams;
  const bottomRowTeams = useTwoRows ? teams.slice(midpoint) : [];

  return (
    <div
      ref={containerRef}
      className={`teamBannerCompact ${
        bottomRowTeams.length > 0 ? 'two-rows' : ''
      }`}
    >
      <Row style={{ height: '100%' }}>
        <Col
          xs={12}
          className={`scoreContainer vCenter ${
            bottomRowTeams.length > 0 ? 'two-rows' : ''
          }`}
        >
          <ul>
            {topRowTeams.map((team, index) => {
              const { teamId, finalScore, parsedLogo } = team || {};

              return (
                <li key={`${teamId}_${index}`}>
                  <span key={`${teamId}`}>
                    <img
                      className="scoreboardHeaderLogo"
                      src={`${MEDIAURL}${parsedLogo}`}
                      alt=""
                    />
                    {finalScore || '0.000'}
                  </span>
                </li>
              );
            })}
          </ul>
          {bottomRowTeams.length > 0 && (
            <ul>
              {bottomRowTeams.map((team, index) => {
                const { teamId, finalScore, parsedLogo } = team || {};

                return (
                  <li key={`${teamId}_${index}`}>
                    <span key={`${teamId}`}>
                      <img
                        className="scoreboardHeaderLogo"
                        src={`${MEDIAURL}${parsedLogo}`}
                        alt=""
                      />
                      {finalScore || '0.000'}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </Col>
      </Row>
    </div>
  );
}

export function TeamBannerCompactBeta() {
  const sessionId = useSelector((state) => state.session.id);
  const teamsData = useTeamsByApparatusData();

  // Use role-based session query to handle authorization properly
  const session = useSession(sessionId);
  const lineups = useLineups(sessionId);

  // Memoize teams data with pre-parsed logos to avoid re-parsing on every render
  const teams = useMemo(() => {
    if (!session?.id) return [];
    const scoreData = teamsData(session);
    return scoreData.teams
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((team) => ({
        ...team,
        parsedLogo:
          (team.logos && JSON.parse(team.logos)?.metaData?.filename) || null,
      }));
  }, [session, teamsData]);

  // Use space-aware layout measurement (compact teams with scorecards need ~140px each)
  const { useTwoRows, containerRef } = useTeamLayoutMeasure({
    teamCount: teams.length,
    minTeamWidth: 140, // Wider for scorecard display
    breakToTwoRowsAtTeams: 6, // Only force two rows at 6+ teams
  });

  // Skip rendering if session data is not available yet
  if (!session || !session.id) {
    return null;
  }

  const midpoint = Math.ceil(teams.length / 2);
  const topRowTeams = useTwoRows ? teams.slice(0, midpoint) : teams;
  const bottomRowTeams = useTwoRows ? teams.slice(midpoint) : [];

  return (
    <div
      ref={containerRef}
      className={`teamBannerCompact ${
        bottomRowTeams.length > 0 ? 'two-rows' : ''
      }`}
    >
      <Row style={{ height: '100%' }}>
        <Col
          xs={12}
          className={`scoreContainer vCenter ${
            bottomRowTeams.length > 0 ? 'two-rows' : ''
          }`}
        >
          <ul>
            {topRowTeams.map((team, index) => {
              const lineup = lineups?.[team?.lineupId];
              const { parsedLogo } = team || {};
              const teamId = lineup?.teamId;

              return (
                <li key={`${teamId}_${index}`}>
                  <span key={`${teamId}`}>
                    <img
                      className="scoreboardHeaderLogo"
                      src={`${MEDIAURL}${parsedLogo}`}
                      alt=""
                    />
                    <TeamScoreCard
                      team={team}
                      lineup={lineup}
                      showLineupLabel={isIntrasquad(session)}
                    />
                  </span>
                </li>
              );
            })}
          </ul>
          {bottomRowTeams.length > 0 && (
            <ul>
              {bottomRowTeams.map((team, index) => {
                const lineup = lineups?.[team?.lineupId];
                const { parsedLogo } = team || {};
                const teamId = lineup?.teamId;

                return (
                  <li key={`${teamId}_${index}`}>
                    <span key={`${teamId}`}>
                      <img
                        className="scoreboardHeaderLogo"
                        src={`${MEDIAURL}${parsedLogo}`}
                        alt=""
                      />
                      <TeamScoreCard
                        team={team}
                        lineup={lineup}
                        showLineupLabel={isIntrasquad(session)}
                      />
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </Col>
      </Row>
    </div>
  );
}

export function TeamBannerLarge() {
  const sessionId = useSelector((state) => state.session.id);
  const teamsData = useTeamsByApparatusData();

  // Use role-based session query to handle authorization properly
  const session = useSession(sessionId);

  // Memoize teams data with pre-parsed logos to avoid re-parsing on every render
  const teams = useMemo(() => {
    if (!session?.id) return [];
    const scoreData = teamsData(session);
    return scoreData.teams
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((team) => ({
        ...team,
        parsedLogo:
          (team.logos && JSON.parse(team.logos)?.metaData?.filename) || null,
      }));
  }, [session, teamsData]);

  // Use space-aware layout measurement (large teams need ~200px each)
  const { useTwoRows, containerRef } = useTeamLayoutMeasure({
    teamCount: teams.length,
    minTeamWidth: 120, // Allow more teams to fit on one row
    breakToTwoRowsAtTeams: 8, // Force two rows at 8+ teams for large banner
  });

  // Skip rendering if session data is not available yet
  if (!session || !session.id) {
    return null;
  }

  const midpoint = Math.ceil(teams.length / 2);
  const topRowTeams = useTwoRows ? teams.slice(0, midpoint) : teams;
  const bottomRowTeams = useTwoRows ? teams.slice(midpoint) : [];
  const extraTeams = Math.max(teams.length - 4, 0);
  const fontSize = 3 - 0.25 * extraTeams;
  const logoSize = 4 - 0.3 * extraTeams;
  const liPadding = Math.max(1 - 0.1 * extraTeams, 0.25);
  const imgPadding = useTwoRows ? 1 : 0.5;

  return (
    <div
      ref={containerRef}
      className={`teamBannerLarge vCenter ${
        bottomRowTeams.length > 0 ? 'two-rows' : ''
      }`}
    >
      <Row style={{ height: '100%' }}>
        <Col xs={12}>
          <ul>
            {topRowTeams.map((team, index) => {
              const { teamId, finalScore, parsedLogo } = team || {};

              return (
                <li
                  key={`${teamId}_${index}`}
                  style={{
                    width: `${95 / topRowTeams.length}%`,
                    padding: `${liPadding}rem`,
                  }}
                >
                  <span
                    key={`${teamId}`}
                    style={{ fontSize: `${fontSize}rem` }}
                  >
                    <img
                      className={`${producerStyles.scoreboardHeaderLogo} scoreboardHeaderLogo`}
                      src={`${MEDIAURL}${parsedLogo}`}
                      alt=""
                      style={{
                        padding: `${imgPadding}rem`,
                        height: `${logoSize}rem`,
                        width: `${logoSize}rem`,
                      }}
                    />
                    {finalScore || '0.000'}
                  </span>
                </li>
              );
            })}
          </ul>
          {bottomRowTeams.length > 0 && (
            <ul>
              {bottomRowTeams.map((team, index) => {
                const { teamId, finalScore, parsedLogo } = team || {};

                return (
                  <li
                    key={`${teamId}_${index}`}
                    style={{ width: `${95 / bottomRowTeams.length}%` }}
                  >
                    <span
                      key={`${teamId}`}
                      style={{ fontSize: `${fontSize}rem` }}
                    >
                      <img
                        className={`${producerStyles.scoreboardHeaderLogo} scoreboardHeaderLogo`}
                        src={`${MEDIAURL}${parsedLogo}`}
                        alt=""
                        style={{ padding: `${imgPadding}rem` }}
                      />
                      {finalScore || '0.000'}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </Col>
      </Row>
    </div>
  );
}
